import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { ai } from '@repletoryreach/sdk-api';
import { Card, Stack, Text, Title, Button, Group } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { Textarea } from '@repletoryreach/sdk-ui/inputs';
import { useT } from '../../i18n/I18nProvider';
import { AiError, AiOutput, ModelSelect, PAGE_FORMAT } from './shared';

/**
 * Ask your documents (14 October 2026): an answer only from the business's knowledge base, with the passages it
 * came from, and "your documents do not say" rather than a guess.
 */
export function AskTab() {
  const t = useT();
  const [question, setQuestion] = useState('');
  const [model, setModel] = useState('');
  const ask = useMutation({ mutationFn: () => ai.answer({ question: question.trim(), model: model || null, format: PAGE_FORMAT }) });
  const result = ask.data?.data;

  return (
    <Stack gap="lg">
      <Card withBorder>
        <form onSubmit={(e) => { e.preventDefault(); if (question.trim()) ask.mutate(); }}>
          <Stack gap="md">
            <Title order={4}>{t('ai.ask.title')}</Title>
            <Text role="bodySmall" c="var(--rr-color-text-muted)">
              {t('ai.ask.body')} <Link to="/knowledge">{t('nav.knowledge')}</Link>
            </Text>
            <Textarea label={t('ai.ask.question')} description={t('ai.ask.questionHint')} value={question}
              minRows={3} autosize onChange={(e) => setQuestion(e.currentTarget.value)} required />
            <ModelSelect value={model} onChange={setModel} />
            <Group justify="flex-end">
              <Button type="submit" loading={ask.isPending} disabled={!question.trim()} data-testid="ai-ask-run">{t('ai.ask.run')}</Button>
            </Group>
            <Text role="caption" c="var(--rr-color-text-muted)">{t('ai.chargeNote')}</Text>
          </Stack>
        </form>
      </Card>
      <AiError error={ask.error} />
      {result && (
        <Card withBorder>
          <Stack gap="md">
            {result.unsure
              ? <Alert color="info" data-testid="ai-unsure">{t('ai.ask.unsure')}</Alert>
              : <AiOutput result={result} />}
            {(result.sources?.length ?? 0) > 0 && (
              <Stack gap={6}>
                <Text role="bodySmall" fw={650}>{t('ai.ask.sources')}</Text>
                {result.sources!.map((s, i) => (
                  <div key={`${s.document_id}-${i}`} className="rr-ai__source">
                    <Text role="caption" fw={600}>{t('ai.ask.source', { name: s.name, page: s.page })}</Text>
                    <Text role="caption" c="var(--rr-color-text-muted)">{s.excerpt}</Text>
                  </div>
                ))}
              </Stack>
            )}
          </Stack>
        </Card>
      )}
    </Stack>
  );
}
